import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Calendar, Target, DollarSign, PieChart as PieChartIcon, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

// Design Constants
const COLORS = {
  beige: '#F5F3ED',
  navy: '#1a1a3e',
  orange: '#FF8C42',
  orangeLight: '#FFB366',
  white: '#ffffff',
  gray: {
    100: '#f5f7f8',
    200: '#e8ecee',
    300: '#c3cacf',
    400: '#9ba4ab',
    500: '#6b7280',
    600: '#4c5b62'
  },
  success: '#10b981',
  error: '#ef4444'
};

// Portfolio data with asset allocation and holdings
const portfolioData = {
  'Velocity': {
    allocation: [
      { category: 'Equities', percentage: 40, color: '#3B82F6' },
      { category: 'Private Equity', percentage: 20, color: '#8B5CF6' },
      { category: 'Venture Capital', percentage: 15, color: '#EC4899' },
      { category: 'Real Estate', percentage: 10, color: '#10B981' },
      { category: 'Alternatives', percentage: 15, color: '#F59E0B' }
    ],
    holdings: [
      { symbol: 'NVDA', name: 'Nvidia Corporation', allocation: 6.0, sector: 'Technology' },
      { symbol: 'MSFT', name: 'Microsoft Corporation', allocation: 5.5, sector: 'Technology' },
      { symbol: 'AAPL', name: 'Apple Inc.', allocation: 5.0, sector: 'Technology' },
      { symbol: 'AMZN', name: 'Amazon.com Inc.', allocation: 4.5, sector: 'Consumer' },
      { symbol: 'GOOGL', name: 'Alphabet Inc.', allocation: 4.0, sector: 'Technology' },
      { symbol: 'META', name: 'Meta Platforms Inc.', allocation: 4.0, sector: 'Technology' },
      { symbol: 'TSLA', name: 'Tesla Inc.', allocation: 3.5, sector: 'Consumer' },
      { symbol: 'QQQ', name: 'Invesco QQQ Trust', allocation: 7.5, sector: 'Technology' },
      { symbol: 'PE-TECH1', name: 'Sequoia Capital Growth Fund', allocation: 10.0, sector: 'Private Equity' },
      { symbol: 'PE-TECH2', name: 'Thoma Bravo Fund XV', allocation: 10.0, sector: 'Private Equity' },
      { symbol: 'VC-SEED1', name: 'Andreessen Horowitz Fund VII', allocation: 8.0, sector: 'Venture Capital' },
      { symbol: 'VC-EARLY1', name: 'Accel Growth Fund IV', allocation: 7.0, sector: 'Venture Capital' },
      { symbol: 'VNQ', name: 'Vanguard Real Estate ETF', allocation: 6.0, sector: 'Real Estate' },
      { symbol: 'REIT-TECH', name: 'Data Centre REIT Fund', allocation: 4.0, sector: 'Real Estate' },
      { symbol: 'CRYPTO-1', name: 'Crypto Index Fund', allocation: 8.0, sector: 'Digital Assets' },
      { symbol: 'INFRA-TECH', name: 'Tech Infrastructure Fund', allocation: 7.0, sector: 'Infrastructure' }
    ],
    explanation: 'Velocity is designed for long-term capital appreciation with a focus on high-growth technology and innovation. The substantial equity exposure (40%) combined with private equity (20%) and venture capital (15%) positions this portfolio to capture outsized returns over extended time horizons. This aggressive growth strategy is ideal for goals with 10+ year timelines like early retirement or wealth accumulation.'
  },
  'Horizon': {
    allocation: [
      { category: 'Equities', percentage: 35, color: '#3B82F6' },
      { category: 'Private Equity', percentage: 15, color: '#8B5CF6' },
      { category: 'Real Estate', percentage: 15, color: '#10B981' },
      { category: 'Fixed Income', percentage: 15, color: '#F59E0B' },
      { category: 'Alternatives', percentage: 20, color: '#6366F1' }
    ],
    holdings: [
      { symbol: 'SPY', name: 'SPDR S&P 500 ETF', allocation: 12.0, sector: 'Equities' },
      { symbol: 'QQQ', name: 'Invesco QQQ Trust', allocation: 10.0, sector: 'Technology' },
      { symbol: 'VEA', name: 'Vanguard FTSE Developed Markets', allocation: 8.0, sector: 'International' },
      { symbol: 'IWM', name: 'iShares Russell 2000', allocation: 5.0, sector: 'Small Cap' },
      { symbol: 'PE-FUND1', name: 'KKR Global Impact Fund', allocation: 8.0, sector: 'Private Equity' },
      { symbol: 'PE-FUND2', name: 'Apollo Growth Equity Fund', allocation: 7.0, sector: 'Private Equity' },
      { symbol: 'VNQ', name: 'Vanguard Real Estate ETF', allocation: 8.0, sector: 'Real Estate' },
      { symbol: 'REIT-PE2', name: 'Starwood Real Estate Fund', allocation: 7.0, sector: 'Real Estate' },
      { symbol: 'AGG', name: 'iShares Core U.S. Aggregate Bond', allocation: 10.0, sector: 'Fixed Income' },
      { symbol: 'VCIT', name: 'Vanguard Intermediate-Term Corp', allocation: 5.0, sector: 'Fixed Income' },
      { symbol: 'GLD', name: 'SPDR Gold Shares', allocation: 8.0, sector: 'Commodities' },
      { symbol: 'PDBC', name: 'Invesco Optimum Yield Diversified', allocation: 6.0, sector: 'Commodities' },
      { symbol: 'INFRA', name: 'Infrastructure Fund', allocation: 6.0, sector: 'Infrastructure' }
    ],
    explanation: 'Horizon strikes a balance between growth and stability through diversified exposure to public and private markets. With 35% in equities and meaningful allocations to private equity, real estate, and alternatives, this portfolio aims for steady appreciation while managing downside risk. The balanced approach makes it suitable for medium to long-term goals requiring consistent growth with moderate volatility.'
  },
  'Fortress': {
    allocation: [
      { category: 'Fixed Income', percentage: 35, color: '#F59E0B' },
      { category: 'Real Estate', percentage: 20, color: '#10B981' },
      { category: 'Equities', percentage: 20, color: '#3B82F6' },
      { category: 'Alternatives', percentage: 15, color: '#6366F1' },
      { category: 'Cash', percentage: 10, color: '#9BA4AB' }
    ],
    holdings: [
      { symbol: 'BND', name: 'Vanguard Total Bond Market', allocation: 15.0, sector: 'Fixed Income' },
      { symbol: 'VCIT', name: 'Vanguard Intermediate-Term Corp', allocation: 10.0, sector: 'Fixed Income' },
      { symbol: 'TIP', name: 'iShares TIPS Bond ETF', allocation: 10.0, sector: 'Fixed Income' },
      { symbol: 'VNQ', name: 'Vanguard Real Estate ETF', allocation: 10.0, sector: 'Real Estate' },
      { symbol: 'REIT-PE1', name: 'Blackstone Real Estate Fund', allocation: 10.0, sector: 'Real Estate' },
      { symbol: 'VTI', name: 'Vanguard Total Stock Market', allocation: 10.0, sector: 'Equities' },
      { symbol: 'SCHD', name: 'Schwab U.S. Dividend Equity', allocation: 10.0, sector: 'Equities' },
      { symbol: 'IAU', name: 'iShares Gold Trust', allocation: 8.0, sector: 'Commodities' },
      { symbol: 'DBC', name: 'Invesco DB Commodity Index', allocation: 7.0, sector: 'Commodities' },
      { symbol: 'SGOV', name: 'iShares 0-3 Month Treasury', allocation: 10.0, sector: 'Fixed Income' }
    ],
    explanation: 'Fortress prioritizes capital preservation while generating modest growth through a defensive allocation strategy. With 35% in fixed income and 20% in real estate, this portfolio emphasizes stability and income generation. The conservative approach with higher cash reserves makes it ideal for shorter-term goals or risk-averse investors seeking wealth preservation with controlled volatility.'
  }
};

// Mock Goals Data with historical performance
const mockGoalsData = {
  '1': {
    id: '1',
    name: 'Beach House',
    description: 'Beachfront property with pool',
    targetAmount: 2250000,
    currentAmount: 450000,
    targetDate: '2035-03-01',
    portfolio: 'Horizon',
    portfolioReturn: '+11.3%',
    progress: 20,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/767a8grf_image.webp',
    monthlyContribution: 3500,
    projectedCompletion: '2034-11-01',
    historicalData: [
      { month: 'Jan 2024', value: 325000, contributions: 42000, returns: 18500 },
      { month: 'Feb 2024', value: 345000, contributions: 45500, returns: 19200 },
      { month: 'Mar 2024', value: 368000, contributions: 49000, returns: 21300 },
      { month: 'Apr 2024', value: 385000, contributions: 52500, returns: 19800 },
      { month: 'May 2024', value: 402000, contributions: 56000, returns: 18700 },
      { month: 'Jun 2024', value: 422000, contributions: 59500, returns: 23200 },
      { month: 'Jul 2024', value: 438000, contributions: 63000, returns: 19500 },
      { month: 'Aug 2024', value: 450000, contributions: 66500, returns: 17200 }
    ]
  },
  '2': {
    id: '2',
    name: 'Financial Freedom',
    description: 'Early retirement fund',
    targetAmount: 3500000,
    currentAmount: 750000,
    targetDate: '2040-01-01',
    portfolio: 'Velocity',
    portfolioReturn: '+13.8%',
    progress: 21,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/ex57r7mr_nick-fewings-GpplHQ-fyvA-unsplash.jpg',
    monthlyContribution: 5000,
    projectedCompletion: '2039-08-01',
    historicalData: [
      { month: 'Jan 2024', value: 620000, contributions: 60000, returns: 28000 },
      { month: 'Feb 2024', value: 645000, contributions: 65000, returns: 29500 },
      { month: 'Mar 2024', value: 672000, contributions: 70000, returns: 31200 },
      { month: 'Apr 2024', value: 693000, contributions: 75000, returns: 27800 },
      { month: 'May 2024', value: 715000, contributions: 80000, returns: 29300 },
      { month: 'Jun 2024', value: 738000, contributions: 85000, returns: 30100 },
      { month: 'Jul 2024', value: 755000, contributions: 90000, returns: 24500 },
      { month: 'Aug 2024', value: 750000, contributions: 95000, returns: 18200 }
    ]
  },
  '3': {
    id: '3',
    name: "Children's Education",
    description: 'Future education fund',
    targetAmount: 850000,
    currentAmount: 425000,
    targetDate: '2028-06-01',
    portfolio: 'Fortress',
    portfolioReturn: '+9.1%',
    progress: 50,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/bjmak8cv_note-thanun-CYlPykF-qAM-unsplash.jpg',
    monthlyContribution: 8000,
    projectedCompletion: '2027-12-01',
    historicalData: [
      { month: 'Jan 2024', value: 345000, contributions: 96000, returns: 15200 },
      { month: 'Feb 2024', value: 358000, contributions: 104000, returns: 14800 },
      { month: 'Mar 2024', value: 372000, contributions: 112000, returns: 16500 },
      { month: 'Apr 2024', value: 384000, contributions: 120000, returns: 13200 },
      { month: 'May 2024', value: 397000, contributions: 128000, returns: 14700 },
      { month: 'Jun 2024', value: 410000, contributions: 136000, returns: 15300 },
      { month: 'Jul 2024', value: 422000, contributions: 144000, returns: 12800 },
      { month: 'Aug 2024', value: 425000, contributions: 152000, returns: 9500 }
    ]
  }
};

// Donut Chart Component (matching dashboard style)
const DonutChart = ({ data, size = 300, total }) => {
  const strokeWidth = size * 0.12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const centerX = size / 2;
  const centerY = size / 2;
  
  let cumulativePercent = 0;
  
  const getLabelPosition = (startPercent, percent) => {
    const midPercent = startPercent + percent / 2;
    const angle = (midPercent / 100) * 2 * Math.PI - Math.PI / 2;
    const labelRadius = radius;
    return {
      x: centerX + labelRadius * Math.cos(angle),
      y: centerY + labelRadius * Math.sin(angle)
    };
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size}>
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={COLORS.gray[200]}
          strokeWidth={strokeWidth}
        />
        
        <g transform={`rotate(-90 ${centerX} ${centerY})`}>
          {data.map((item, index) => {
            const percent = item.percentage;
            const offset = (cumulativePercent / 100) * circumference;
            const dashArray = `${(percent / 100) * circumference} ${circumference}`;
            const startPercent = cumulativePercent;
            cumulativePercent += percent;
            
            return (
              <circle
                key={index}
                cx={centerX}
                cy={centerY}
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth={strokeWidth}
                strokeDasharray={dashArray}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                style={{ transition: 'all 0.3s ease' }}
              />
            );
          })}
        </g>
        
        {(() => {
          let cumulative = 0;
          return data.map((item, index) => {
            const startPercent = cumulative;
            const pos = getLabelPosition(startPercent, item.percentage);
            cumulative += item.percentage;
            
            return (
              <text
                key={`label-${index}`}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fill: 'white',
                  fontSize: `${size * 0.055}px`,
                  fontWeight: 'bold',
                  pointerEvents: 'none',
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}
              >
                {item.percentage}%
              </text>
            );
          });
        })()}
      </svg>
      
      {/* Center Amount */}
      {total && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: `${size * 0.08}px`,
            fontWeight: '600',
            color: COLORS.navy,
            fontFamily: 'Georgia, serif',
            lineHeight: 1
          }}>
            {formatCurrency(total)}
          </div>
          <div style={{
            fontSize: `${size * 0.04}px`,
            color: COLORS.gray[500],
            marginTop: '0.25rem'
          }}>
            Invested
          </div>
        </div>
      )}
    </div>
  );
};

const GoalDetail = () => {
  const { goalId } = useParams();
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState('overview');
  
  const goal = mockGoalsData[goalId];
  const portfolio = portfolioData[goal?.portfolio];

  if (!goal) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4" style={{ backgroundColor: COLORS.beige }}>
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Goal Not Found</h1>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const maxValue = Math.max(...goal.historicalData.map(d => d.value));

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.beige, paddingTop: '80px' }}>
      {/* Header with Image */}
      <div style={{ position: 'relative', height: '300px', overflow: 'hidden' }}>
        <img 
          src={goal.image} 
          alt={goal.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7)'
          }}
        />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: '2rem'
        }}>
          <div className="max-w-7xl mx-auto w-full">
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                color: COLORS.white,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
                backdropFilter: 'blur(10px)'
              }}
            >
              <ArrowLeft size={20} />
              Back to Dashboard
            </button>
            <h1 style={{
              fontSize: '3.5rem',
              fontWeight: '300',
              color: COLORS.white,
              fontFamily: 'Georgia, serif',
              marginBottom: '0.5rem',
              lineHeight: 1.1
            }}>
              {goal.name}
            </h1>
            <p style={{ fontSize: '1.125rem', color: 'rgba(255,255,255,0.8)' }}>
              {goal.description}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: COLORS.white,
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <DollarSign size={20} color={COLORS.gray[400]} />
              <span style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>Current Balance</span>
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: COLORS.navy,
              fontFamily: 'Georgia, serif'
            }}>
              {formatCurrency(goal.currentAmount)}
            </div>
          </div>

          <div style={{
            background: COLORS.white,
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Target size={20} color={COLORS.gray[400]} />
              <span style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>Target Amount</span>
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: COLORS.navy,
              fontFamily: 'Georgia, serif'
            }}>
              {formatCurrency(goal.targetAmount)}
            </div>
          </div>

          <div style={{
            background: COLORS.white,
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <TrendingUp size={20} color={COLORS.success} />
              <span style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>Portfolio Return</span>
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: COLORS.success,
              fontFamily: 'Georgia, serif'
            }}>
              {goal.portfolioReturn}
            </div>
          </div>

          <div style={{
            background: COLORS.white,
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Calendar size={20} color={COLORS.gray[400]} />
              <span style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>Target Date</span>
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: COLORS.navy,
              fontFamily: 'Georgia, serif'
            }}>
              {formatDate(goal.targetDate)}
            </div>
          </div>
        </div>

        {/* NEW: Portfolio Overview Section */}
        <Card style={{ marginBottom: '2rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <PieChartIcon size={24} color={COLORS.navy} />
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '300',
              color: COLORS.navy,
              fontFamily: 'Georgia, serif'
            }}>
              {goal.portfolio} Portfolio
            </h2>
          </div>

          {/* Portfolio Assignment Info */}
          <div style={{
            background: `${COLORS.orange}10`,
            padding: '1.25rem',
            borderRadius: '0.75rem',
            marginBottom: '2rem',
            borderLeft: `4px solid ${COLORS.orange}`
          }}>
            <p style={{
              fontSize: '1rem',
              color: COLORS.navy,
              lineHeight: '1.6'
            }}>
              <strong>This goal is linked to the {goal.portfolio} Portfolio.</strong>
              <br />
              <span style={{ fontSize: '0.9rem', color: COLORS.gray[600] }}>
                {portfolio.explanation}
              </span>
            </p>
          </div>

          {/* Asset Allocation Donut Chart */}
          <div style={{ marginBottom: '2.5rem' }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '500',
              color: COLORS.navy,
              marginBottom: '1.5rem',
              fontFamily: 'Georgia, serif'
            }}>
              Asset Allocation
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <DonutChart data={portfolio.allocation} size={300} total={goal.currentAmount} />
              
              {/* Legend */}
              <div style={{ 
                marginTop: '2rem',
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem',
                width: '100%',
                maxWidth: '600px'
              }}>
                {portfolio.allocation.map((asset, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '4px',
                      background: asset.color,
                      flexShrink: 0
                    }} />
                    <div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: COLORS.gray[600],
                        fontWeight: '500'
                      }}>
                        {asset.category}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: COLORS.gray[400]
                      }}>
                        {asset.percentage}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Underlying Assets Breakdown */}
          <div>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '500',
              color: COLORS.navy,
              marginBottom: '1rem',
              fontFamily: 'Georgia, serif'
            }}>
              Underlying Assets
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: COLORS.gray[500],
              marginBottom: '1.5rem'
            }}>
              These are the specific investments that make up your portfolio:
            </p>

            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: `1px solid ${COLORS.gray[200]}`,
              borderRadius: '0.75rem'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ 
                  background: COLORS.gray[100],
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  <tr>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: COLORS.gray[600],
                      borderBottom: `1px solid ${COLORS.gray[200]}`
                    }}>Symbol</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: COLORS.gray[600],
                      borderBottom: `1px solid ${COLORS.gray[200]}`
                    }}>Asset Name</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: COLORS.gray[600],
                      borderBottom: `1px solid ${COLORS.gray[200]}`
                    }}>Sector</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'right',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: COLORS.gray[600],
                      borderBottom: `1px solid ${COLORS.gray[200]}`
                    }}>Allocation</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.holdings.map((holding, index) => (
                    <tr key={index} style={{
                      borderBottom: index < portfolio.holdings.length - 1 ? `1px solid ${COLORS.gray[200]}` : 'none'
                    }}>
                      <td style={{
                        padding: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: COLORS.navy
                      }}>{holding.symbol}</td>
                      <td style={{
                        padding: '1rem',
                        fontSize: '0.875rem',
                        color: COLORS.gray[600]
                      }}>{holding.name}</td>
                      <td style={{
                        padding: '1rem',
                        fontSize: '0.875rem',
                        color: COLORS.gray[500]
                      }}>{holding.sector}</td>
                      <td style={{
                        padding: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: COLORS.navy,
                        textAlign: 'right'
                      }}>{holding.allocation.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {/* Progress Section */}
        <Card style={{ marginBottom: '2rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '300',
            color: COLORS.navy,
            marginBottom: '1.5rem',
            fontFamily: 'Georgia, serif'
          }}>
            Progress to Goal
          </h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>Progress</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: COLORS.navy }}>
                {goal.progress}% Complete
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '12px',
              background: COLORS.gray[200],
              borderRadius: '6px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${goal.progress}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
                borderRadius: '6px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '1.5rem',
            marginTop: '2rem'
          }}>
            <div>
              <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.5rem' }}>
                Monthly Contribution
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.navy }}>
                {formatCurrency(goal.monthlyContribution)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.5rem' }}>
                Total Contributions
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.navy }}>
                {formatCurrency(goal.historicalData[goal.historicalData.length - 1].contributions)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.5rem' }}>
                Investment Returns
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.success }}>
                {formatCurrency(goal.historicalData[goal.historicalData.length - 1].returns)}
              </div>
            </div>
          </div>
        </Card>

        {/* Performance Chart */}
        <Card style={{ padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '300',
            color: COLORS.navy,
            marginBottom: '1.5rem',
            fontFamily: 'Georgia, serif'
          }}>
            Goal Performance Over Time
          </h2>

          <div style={{ position: 'relative', height: '300px', marginTop: '2rem' }}>
            <svg style={{ width: '100%', height: '100%' }}>
              {/* Grid lines */}
              {[0, 1, 2, 3, 4].map((i) => (
                <line
                  key={i}
                  x1="0"
                  y1={`${(i / 4) * 100}%`}
                  x2="100%"
                  y2={`${(i / 4) * 100}%`}
                  stroke={COLORS.gray[200]}
                  strokeWidth="1"
                />
              ))}
              
              {/* Area chart */}
              <defs>
                <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.orange} stopOpacity="0.3" />
                  <stop offset="100%" stopColor={COLORS.orange} stopOpacity="0.05" />
                </linearGradient>
              </defs>
              
              <path
                d={(() => {
                  const points = goal.historicalData.map((d, i) => {
                    const x = (i / (goal.historicalData.length - 1)) * 100;
                    const y = 100 - ((d.value / maxValue) * 100);
                    return `${x},${y}`;
                  });
                  const path = `M ${points.join(' L ')} L 100,100 L 0,100 Z`;
                  return path;
                })()}
                fill="url(#areaGradient)"
              />
              
              {/* Line */}
              <polyline
                points={goal.historicalData.map((d, i) => {
                  const x = (i / (goal.historicalData.length - 1)) * 100;
                  const y = 100 - ((d.value / maxValue) * 100);
                  return `${x}%,${y}%`;
                }).join(' ')}
                fill="none"
                stroke={COLORS.orange}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* Points */}
              {goal.historicalData.map((d, i) => {
                const x = (i / (goal.historicalData.length - 1)) * 100;
                const y = 100 - ((d.value / maxValue) * 100);
                return (
                  <circle
                    key={i}
                    cx={`${x}%`}
                    cy={`${y}%`}
                    r="4"
                    fill={COLORS.white}
                    stroke={COLORS.orange}
                    strokeWidth="2"
                  />
                );
              })}
            </svg>
          </div>

          {/* Month labels */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            {goal.historicalData.map((d, i) => (
              <span key={i} style={{ fontSize: '0.75rem', color: COLORS.gray[500] }}>
                {d.month.split(' ')[0]}
              </span>
            ))}
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <Button
            style={{
              background: COLORS.orange,
              color: COLORS.white,
              padding: '1rem 2rem',
              fontSize: '1rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Deposit
          </Button>
          <Button
            variant="outline"
            style={{
              borderColor: COLORS.navy,
              color: COLORS.navy,
              padding: '1rem 2rem',
              fontSize: '1rem',
              borderRadius: '0.5rem'
            }}
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoalDetail;
